import { useEffect, useState } from 'react';
import { useAddItemsToCart, useCart } from 'hooks';
import classNames from 'classnames';
import { useSettings } from 'contexts/SettingContext';
import useProductAddToCartTracking from 'hooks/tracking/useProductAddToCartTracking';
import { useRouter } from 'next/router';
import useProductAddedTracking from 'hooks/tracking/useProductAddedTracking';
import { getShopifyGid } from 'lib/shopify/shopifyGid';
import {
  getSelectedSalonNo,
  getStorageCustomerSalonNo,
  formatPriceByStoreLocale,
  getCurrencyCode,
} from 'lib/shopify';
import SiteLink from 'components/shared/sitelink/sitelink';
import { addLocaleToHref } from 'lib/utils/helpers';
import ItemCounter from '../itemCounter';
import BackInStockNotifier from '../backInStockNotifier';
import styles from './cartSection.module.scss';

const CartSection = ({ className, resources, shopifyData, productData, locale, sellingPlan }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const router = useRouter();
  const addItems = useAddItemsToCart();
  const { addProduct, trackedItemsSelectList } = useSettings();
  const salonNo = getStorageCustomerSalonNo(locale);
  const selectedSalonNo = getSelectedSalonNo(locale);

  // This is to initialize the cart when the customer is navigating to a product page
  const { cart } = useCart();
  const {
    addToCart,
    addingToCart,
    emailWhenAvailable,
    linkToAnotherProductMessage,
    linkToAnotherProductButtonText,
  } = resources.productpage;
  const {
    freeItem,
    amountOfProductsToGetFreeItem,
    promoCollectionName,
    shopifyTagsToExcludeFromPromo,
  } = resources.secondpartsettings;
  const outOfStock = shopifyData?.outOfStock;

  const calcSubscriptionPrice = productPrice => {
    if (!sellingPlan?.priceAdjustments) return null;
    const { priceAdjustments } = sellingPlan;

    if (priceAdjustments.price?.amount) {
      return priceAdjustments.price?.amount;
    }

    if (priceAdjustments.adjustmentPercentage) {
      return (productPrice * (100 - priceAdjustments.adjustmentPercentage)) / 100;
    }

    if (priceAdjustments.adjustmentAmount?.amount) {
      return productPrice - priceAdjustments.adjustmentAmount.amount;
    }
  };

  const getButtonText = () => {
    if (isLoading) {
      return addingToCart?.text;
    }

    const productPrice = shopifyData?.productPrice;
    const subscriptionPrice = calcSubscriptionPrice(productPrice);
    const productCompareAtPrice = shopifyData?.productCompareAtPrice;

    if (subscriptionPrice) {
      return (
        <div className={styles.cartButtonText}>
          {addToCart?.text}
          <span>-</span>
          <span>
            {formatPriceByStoreLocale({
              locale,
              price: subscriptionPrice,
            })}
          </span>
          {productPrice && (
            <span className={styles.salesPrice}>
              {formatPriceByStoreLocale({
                locale,
                price: productPrice,
              })}
            </span>
          )}
        </div>
      );
    }

    return (
      <div className={styles.cartButtonText}>
        {addToCart?.text}
        <span>-</span>
        <span>
          {productCompareAtPrice && (
            <s>
              {formatPriceByStoreLocale({
                locale,
                price: productCompareAtPrice,
              })}
            </s>
          )}
          {productPrice &&
            !productCompareAtPrice &&
            `${formatPriceByStoreLocale({
              locale,
              price: productPrice,
            })}`}
        </span>
        {productCompareAtPrice && (
          <span className={styles.salesPrice}>
            {formatPriceByStoreLocale({
              locale,
              price: productPrice,
            })}
          </span>
        )}
      </div>
    );
  };

  const handleAddToCart = async () => {
    setIsLoading(true);
    const productSelectListObj = trackedItemsSelectList?.find(
      p => p?.productId === String(shopifyData?.productId)
    );

    const keuneRepeat = (sellingPlan?.options?.[0]?.value?.split(' ') || [])[0] || 0;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useProductAddToCartTracking({
      product: {
        quantity,
        name: shopifyData?.productTitle,
        id: shopifyData?.productId,
        productSpecsCollection: productData?.productSpecsCollection?.items,
        price: shopifyData?.productPrice,
        itemListName: productSelectListObj?.itemListName,
        currencyCode: getCurrencyCode(locale),
        keune_repeat: parseInt(keuneRepeat, 10),
      },
    });

    // Universal Analytics
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useProductAddedTracking({
      affiliatedSalonNo: salonNo,
      selectedSalonNo,
      product: {
        name: shopifyData?.productTitle,
        id: shopifyData?.productId,
        brand: 'Keune',
        category: productData?.productSpecsCollection?.items?.find(
          ({ type }) =>
            type.filterType === 'Producttype' ||
            type.filterType === 'Type de produit' ||
            type.filterType === 'Product type'
        )?.value,
        price: shopifyData?.productPrice,
        discount: shopifyData?.productCompareAtPrice
          ? shopifyData?.productCompareAtPrice - shopifyData?.productPrice
          : null,
        quantity,
      },
      location: { action: router },
    });

    const relatedProducts = shopifyData.relatedProducts || [];

    const productsDataForCheckout = [
      {
        variantId: shopifyData.variantId,
        quantity,
        sellingPlanId: sellingPlan?.id,
      },
      ...relatedProducts.map(id => ({ variantId: id, quantity })),
    ];

    await addItems(productsDataForCheckout, freeItem?.product?.shopifyData, {
      amountOfProductsToGetFreeItem,
      promoCollectionName,
      shopifyTagsToExcludeFromPromo,
    });

    setIsLoading(false);
  };

  useEffect(() => {
    setQuantity(1);
  }, [router.asPath]);

  if (productData?.linkToAnotherProduct?.slug && productData?.useLinkToAnotherProduct !== false) {
    return (
      <div className={classNames(className, styles.linkToAnotherProduct)}>
        <p>{linkToAnotherProductMessage?.text}</p>
        <SiteLink
          locale={locale}
          className={styles.linkToAnotherButton}
          style={{
            backgroundColor: resources?.btncolors?.linkToAnotherProductColor?.text,
            color: resources?.btncolors?.linkToAnotherProductTextColor?.text,
          }}
          href={addLocaleToHref(productData?.linkToAnotherProduct?.slug, locale, router)}
        >
          {linkToAnotherProductButtonText?.text}
        </SiteLink>
      </div>
    );
  }
  return (
    <div className={classNames(className, styles.cartSection)}>
      {outOfStock ? (
        <BackInStockNotifier
          locale={locale}
          text={emailWhenAvailable.fullRichText}
          successText={emailWhenAvailable.text}
          submitText={emailWhenAvailable.multipleTexts[0]}
          productIds={[shopifyData?.variantId]}
        />
      ) : (
        <>
          <span className={styles.controls}>
            <ItemCounter
              quantity={quantity}
              updateQuantity={value => setQuantity(value)}
              className={styles.itemCounter}
            />
            <button
              type="button"
              disabled={outOfStock}
              className={styles.addToCartButton}
              style={{
                backgroundColor: resources?.btncolors?.addToCartButtonColor?.text,
                color: resources?.btncolors?.addToCartButtonTextColor?.text,
              }}
              onClick={() => {
                handleAddToCart();
                addProduct({
                  title: productData.title,
                  slug: productData.slug,
                  variantId: getShopifyGid('ProductVariant', shopifyData?.variantId),
                });
              }}
            >
              {getButtonText()}
            </button>
          </span>
        </>
      )}
    </div>
  );
};

export default CartSection;
